const CAP_PRECIO_BITCOIN = {
  title: "¿Quién fija el precio de Bitcoin?",
  sections: [
    {
      heading: "No hay un “precio oficial” de Bitcoin",
      text: [
        "Una de las primeras ideas que hay que entender es que **Bitcoin no tiene un precio oficial único**.",
        "No existe un banco central, una empresa o un gobierno que diga: “este es el precio de 1 BTC hoy”.",
        "El precio surge de la **oferta y la demanda** en miles de intercambios, acuerdos privados y plataformas alrededor del mundo.",
        "Cada vez que dos personas se ponen de acuerdo para comprar y vender bitcoin a cierto precio, están contribuyendo a definir el valor de mercado en ese momento."
      ]
    },
    {
      heading: "Cómo se forma el precio en la práctica",
      text: [
        "Imagina a dos personas que quieren intercambiar bitcoin:",
        "Una persona quiere **vender** 1 BTC y propone un precio.",
        "La otra persona quiere **comprar** BTC y decide si acepta, negocia o rechaza ese precio.",
        "Si llegan a un acuerdo, se realiza la operación. Ese valor al que se ha cerrado el trato es el **precio al que se ha negociado ese bitcoin en concreto**.",
        "Este tipo de acuerdos pueden hacerse en persona, entre conocidos, o de forma mucho más habitual en **plataformas de intercambio (exchanges) y mercados de trading online**."
      ]
    },
    {
      heading: "Plataformas de intercambio y mercado global",
      text: [
        "En las plataformas de compraventa de bitcoin, cualquier usuario puede publicar órdenes de compra o venta al precio que considere justo.",
        "Allí se cruzan miles de órdenes de distintos participantes: personas particulares, empresas, fondos, etc.",
        "Cuando una orden de compra coincide con una orden de venta en precio y cantidad, **se ejecuta la operación**.",
        "El último precio al que se ejecutó una operación suele mostrarse como el **precio actual** en esa plataforma.",
        "Este proceso ocurre de forma continua en muchas plataformas distintas al mismo tiempo, en diferentes países y monedas."
      ],
      list: [
        "Puedes comprar fracciones muy pequeñas de bitcoin (no hace falta comprar 1 BTC completo).",
        "1 BTC se divide en 100,000,000 de unidades llamadas **satoshis**.",
        "Es posible comprar importes pequeños, por ejemplo el equivalente a unos pocos euros o pesos."
      ]
    },
    {
      heading: "Por qué no existe un precio único en todo el mundo",
      text: [
        "Como no hay una sola plataforma ni un solo país donde se negocie bitcoin, **los precios pueden variar ligeramente** entre mercados.",
        "En una región donde es más difícil acceder a bitcoin, el precio local puede ser más alto.",
        "En lugares con más competencia y más liquidez, el precio suele estar muy cercano al de otros mercados globales."
      ],
      list: [
        "Las diferencias grandes de precio suelen durar poco tiempo.",
        "Si en un mercado bitcoin está más barato y en otro más caro, algunos usuarios compran en el más barato y venden en el más caro.",
        "A este proceso se le llama **arbitraje**, y tiende a equilibrar los precios entre diferentes plataformas."
      ]
    },
    {
      heading: "Bitcoin como medio de pago y el rol de la adopción",
      text: [
        "Durante sus primeros años, bitcoin prácticamente no tenía valor de mercado: se usaba entre personas curiosas, en foros y comunidades pequeñas.",
        "El precio empezó a tomar forma en el momento en que alguien aceptó **bitcoin a cambio de un bien o servicio real**.",
        "Desde entonces, cada vez que más personas y negocios aceptan bitcoin como medio de pago, aumenta su utilidad percibida.",
        "A mayor utilidad y demanda, mayor presión para que su **precio tienda a subir en el largo plazo**, aunque en el corto plazo pueda ser muy volátil."
      ]
    },
    {
      heading: "Eventos externos que afectan al precio",
      text: [
        "El precio de bitcoin no solo depende de las órdenes de compra y venta, sino también de **noticias y eventos externos**.",
        "Algunos ejemplos de sucesos que pueden influir en el precio:"
      ],
      list: [
        "Noticias sobre regulaciones positivas o negativas en distintos países.",
        "Casos de hackeos o fallos de seguridad en plataformas de intercambio.",
        "Anuncios de grandes empresas que empiezan a aceptar o a invertir en bitcoin.",
        "Cambios tecnológicos importantes en la propia red de Bitcoin.",
        "Ciclos económicos, crisis financieras o pérdida de confianza en monedas tradicionales."
      ]
    },
    {
      heading: "El papel de los mineros en la economía de Bitcoin",
      text: [
        "Los **mineros** son quienes aportan poder computacional para asegurar la red y recibir recompensas en bitcoin.",
        "Ellos no deciden directamente el precio, pero su actividad está ligada a la economía del sistema.",
        "Minar bitcoin tiene costos: electricidad, hardware, mantenimiento, infraestructura.",
        "Si el precio de mercado es demasiado bajo, algunos mineros dejan de ser rentables y desconectan sus equipos.",
        "Si el precio sube mucho, nuevos mineros se sienten atraídos a participar."
      ],
      list: [
        "A largo plazo, el precio de bitcoin suele estar por encima de su **coste de producción promedio**, porque si no, nadie querría minar.",
        "Cuando el premio por bloque se reduce (halving), el sistema obliga a una especie de reajuste económico.",
        "Si la recompensa en BTC se reduce a la mitad, pero el interés por bitcoin sigue creciendo, el mercado tiende a ajustar el precio."
      ]
    },
    {
      heading: "Halvings y oferta limitada",
      text: [
        "Bitcoin tiene una **emisión limitada**: jamás habrá más de 21 millones de BTC.",
        "Cada cierto número de bloques ocurre un evento programado llamado **halving**, en el que la recompensa que reciben los mineros se reduce a la mitad.",
        "Esto significa que, con el tiempo, la cantidad de nuevos bitcoins que entra al mercado se va reduciendo.",
        "Si la demanda se mantiene igual o aumenta mientras la oferta nueva baja, es razonable esperar presión alcista sobre el precio en el largo plazo.",
        "Sin embargo, este efecto no es automático ni garantizado: depende del comportamiento real de los usuarios, inversores y mercados."
      ]
    },
    {
      heading: "Volatilidad: por qué el precio sube y baja tanto",
      text: [
        "Bitcoin es conocido por su **alta volatilidad**: su precio puede subir o bajar de forma rápida en períodos cortos.",
        "Esta volatilidad se explica en parte porque todavía es un activo joven comparado con otros mercados financieros, y porque una parte importante de sus participantes opera de forma especulativa (compra para vender más caro en el futuro).",
        "Cuando se combinan especulación, noticias, sentimiento del mercado y una oferta relativamente limitada, los movimientos de precio pueden ser muy fuertes."
      ],
      list: [
        "La volatilidad asusta a muchas personas en el corto plazo.",
        "A la vez, es lo que ha permitido grandes revalorizaciones en su historia.",
        "Con el tiempo, si la adopción sigue creciendo, algunos analistas esperan que la volatilidad disminuya."
      ]
    },
    {
      heading: "Entonces… ¿quién fija realmente el precio?",
      text: [
        "Después de todo lo anterior, podemos resumir la respuesta:",
        "El precio de bitcoin **no lo fija una sola entidad**. Lo fijan millones de personas y organizaciones que, cada día, deciden a qué precio están dispuestos a comprar o vender.",
        "Cada operación es un pequeño voto sobre cuánto vale 1 BTC en ese momento.",
        "La combinación de todos esos “votos” en mercados abiertos y globales es lo que genera el precio que ves en las gráficas.",
        "Por eso, para entender el precio de bitcoin no basta con mirar una sola cifra: es necesario observar la oferta, la demanda, las noticias, los ciclos económicos y la propia evolución tecnológica de la red."
      ]
    }
  ]
};

export default CAP_PRECIO_BITCOIN;
